import React, { useEffect, useState } from "react";
import { FileText, Loader2, Plus, X, ExternalLink, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { isAdmin, adminHeader } from "../lib/admin";
import { toast } from "sonner";
import FactDebate from "./FactDebate";

const LOCAL_NAME_KEY = "vault.comment.name";

export default function CommunityFacts({ targetType, targetId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [admin, setAdmin] = useState(isAdmin());
  const [fact, setFact] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [name, setName] = useState(() => localStorage.getItem(LOCAL_NAME_KEY) || "");

  useEffect(() => {
    const h = () => setAdmin(isAdmin());
    window.addEventListener("admin-changed", h);
    return () => window.removeEventListener("admin-changed", h);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/community-facts/${targetType}/${targetId}`);
      setItems(r.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!fact.trim() || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/community-facts", {
        target_type: targetType,
        target_id: targetId,
        fact: fact.trim(),
        source_url: sourceUrl.trim() || null,
        author: name.trim() || null,
      });
      setItems((prev) => [r.data, ...prev]);
      setFact("");
      setSourceUrl("");
      if (name.trim()) localStorage.setItem(LOCAL_NAME_KEY, name.trim());
      setOpen(false);
      toast.success("Fact added to community intel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add fact");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this community fact?")) return;
    try {
      await api.delete(`/admin/community-facts/${id}`, { headers: adminHeader() });
      setItems((prev) => prev.filter((f) => f.id !== id));
      toast.success("Fact deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  return (
    <section data-testid="community-facts-section" className="mt-12 border border-white/10 bg-[#0a0a0a] p-6 sm:p-8 print-hide-children">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 flex items-center gap-2">
          <FileText size={11} /> COMMUNITY-SOURCED INTEL // {items.length}
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="add-facts-toggle"
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00ff88]/40 text-[#00ff88] hover:bg-[#00ff88]/10 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors no-print"
        >
          {open ? <X size={12} /> : <Plus size={12} />}
          {open ? "Cancel" : "Add More Facts"}
        </button>
      </div>
      <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-6 border-b border-white/10 pb-3">
        Community-Sourced Intel
      </h3>

      {open && (
        <form
          onSubmit={submit}
          data-testid="community-facts-form"
          className="bg-[#0a0a0a] border border-[#00ff88]/30 p-5 mb-8 space-y-3 no-print"
        >
          <div className="grid sm:grid-cols-[200px_1fr] gap-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Handle (optional)"
              maxLength={40}
              data-testid="fact-author-input"
              className="bg-transparent border border-white/15 px-3 py-2 font-mono text-xs text-white placeholder-zinc-600 focus:border-[#00ff88] focus:outline-none"
            />
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 self-center">
              CITIZEN-SUBMITTED INTEL — REVIEW BEFORE TRUSTING
            </div>
          </div>
          <textarea
            value={fact}
            onChange={(e) => setFact(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Add a documented fact, leaked record, or new lead. Be specific."
            data-testid="fact-content-input"
            className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-xs text-white placeholder-zinc-600 focus:border-[#00ff88] focus:outline-none resize-none"
          />
          <input
            type="url"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://source-url.com (optional but encouraged)"
            data-testid="fact-source-input"
            className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-xs text-white placeholder-zinc-600 focus:border-[#00ff88] focus:outline-none"
          />
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-wider text-zinc-600">
              {fact.length} / 2000 — facts without sources show as UNVERIFIED
            </span>
            <button
              type="submit"
              disabled={!fact.trim() || submitting}
              data-testid="fact-submit"
              className="inline-flex items-center gap-2 bg-[#00ff88] text-[#050505] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
              Submit Fact
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="font-mono text-xs tracking-wider text-zinc-500">LOADING...</div>
      ) : items.length === 0 ? (
        <div className="bg-[#050505] border border-dashed border-white/10 p-8 text-center font-mono text-[10px] tracking-[0.2em] text-zinc-600">
          NO COMMUNITY FACTS YET — DROP THE FIRST RECEIPT.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="community-facts-list">
          {items.map((f) => (
            <li key={f.id} className="border border-white/10 bg-[#0F0F0F] p-4 border-l-2 border-l-[#00ff88]/60">
              <div className="flex items-center justify-between mb-2 font-mono text-[10px] tracking-[0.2em] text-zinc-500">
                <div className="flex items-center gap-3">
                  <span className="text-[#00ff88]">{f.author}</span>
                  <span className={`px-1.5 py-0.5 border ${f.source_url ? "text-blue-400 border-blue-400/40" : "text-[#ff3b30] border-[#ff3b30]/40"}`}>
                    {f.source_url ? "SOURCED" : "UNVERIFIED"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span>{fmt(f.created_at)}</span>
                  {admin && (
                    <button
                      onClick={() => onDelete(f.id)}
                      data-testid={`admin-delete-fact-${f.id}`}
                      className="text-zinc-500 hover:text-[#ff3b30] no-print"
                      aria-label="Delete fact"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap mb-2">{f.fact}</p>
              {f.source_url && (
                <a
                  href={f.source_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-blue-400 hover:text-blue-300 break-all"
                >
                  <ExternalLink size={10} />
                  {f.source_url}
                </a>
              )}
              <FactDebate factId={f.id} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
